import { Button, Dropdown, Typography } from 'antd'
import { Ban, MoreHorizontal, Pencil, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { RevokedStatusTag } from 'src/components'

import { StatusPill } from './CredentialListChrome'

const { Text } = Typography

/**
 * Shared cell renderers for credential list tables. Both the workspace
 * public-access list and the app credentials tab use them — the columns
 * themselves (their identity, order, widths) stay per-page because data
 * shapes differ, but the individual cell renderings are identical.
 */

type NameCellProps = {
  name: string
  to: string
}

/**
 * Name cell: a link to the detail page rendered as plain bold body text —
 * `useCredentialsListStyles.name-link` handles the color and hover state.
 * `Text.ellipsis={{ tooltip }}` truncates with a hover tooltip when the
 * value overflows the column's fixed width.
 */
export const CredentialNameCell = ({ name, to }: NameCellProps) => (
  <Link to={to} className="name-link" style={{ display: 'block', minWidth: 0 }}>
    <Text strong ellipsis={{ tooltip: name }}>
      {name}
    </Text>
  </Link>
)

type StatusCellProps<
  T extends {
    status?: string
    revoked_at?: string | null
    reason?: string | null
  }
> = {
  item: T
  /** Localised label — pass `t('<feature>.status.<status>')` from the caller. */
  statusLabel: string
  /** Tooltip shown over the revoked pill when a revoke reason is present. */
  reasonTip: (reason: string) => string
  /** Tooltip shown over the revoked pill when only a revoke timestamp exists. */
  atTip: (at: string) => string
}

/**
 * Status cell: a soft-tinted pill. When the credential is revoked, wraps
 * the pill in `RevokedStatusTag` so hovering reveals the reason/timestamp.
 */
export const CredentialStatusCell = <
  T extends {
    status?: string
    revoked_at?: string | null
    reason?: string | null
  }
>({
  item,
  statusLabel,
  reasonTip,
  atTip
}: StatusCellProps<T>) => {
  const status = item.status ?? 'active'
  const pill = <StatusPill status={status} label={statusLabel} />

  if (status !== 'revoked') return pill

  return (
    <RevokedStatusTag item={item} reasonTip={reasonTip} atTip={atTip}>
      {pill}
    </RevokedStatusTag>
  )
}

type ActionsCellProps = {
  /** Revoked or expired — disables Edit (only active credentials are editable). */
  locked: boolean
  /** Deliberately revoked — disables Rotate and Revoke (expired keeps both). */
  revoked: boolean
  /** Omit to hide the Edit button. */
  onEdit?: () => void
  onRotate: () => void
  onRevoke: () => void
  rotateLabel: string
  revokeLabel: string
}

/**
 * Actions cell: three buttons — Edit (icon-only, optional), Rotate,
 * Revoke. Edit is disabled once the credential is locked (revoked or
 * expired); Rotate and Revoke stay available until it is revoked.
 */
export const CredentialActionsCell = ({
  locked,
  revoked,
  onEdit,
  onRotate,
  onRevoke,
  rotateLabel,
  revokeLabel
}: ActionsCellProps) => {
  const { t } = useTranslation()

  return (
    <Dropdown
      trigger={['click']}
      menu={{
        items: [
          ...(onEdit
            ? [
                {
                  key: 'edit',
                  icon: <Pencil size={14} />,
                  label: t('edit', 'Edit'),
                  disabled: locked,
                  onClick: onEdit
                }
              ]
            : []),
          {
            key: 'rotate',
            icon: <RefreshCw size={14} />,
            label: rotateLabel,
            disabled: revoked,
            onClick: onRotate
          },
          { type: 'divider' as const },
          {
            key: 'revoke',
            icon: <Ban size={14} />,
            label: revokeLabel,
            danger: true,
            disabled: revoked,
            onClick: onRevoke
          }
        ]
      }}
    >
      <Button
        type="text"
        size="small"
        icon={<MoreHorizontal size={14} />}
        aria-label={t('more', 'More')}
      />
    </Dropdown>
  )
}

/**
 * Row click adapter for AntD Table's `onRow` — navigates to the detail page
 * unless the click owns its own action. Dropdown items are portaled but their
 * clicks still bubble here through React, so exclude `[role="menuitem"]` /
 * `.ant-dropdown` too — else Edit/Rotate/Revoke navigate instead of opening.
 */
export const useCredentialRowClick = <T extends { id: string }>(
  onSelect: (id: string) => void
) => {
  return (record: T) => ({
    style: { cursor: 'pointer' },
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      if (
        (event.target as HTMLElement).closest(
          'a, button, [role="button"], [role="menuitem"], .ant-dropdown'
        )
      ) {
        return
      }
      onSelect(record.id)
    }
  })
}
